/**
 * RoadmapPageLayout — the `/roadmap` build-track journey layout.
 *
 * PRESENTATION ONLY (SPEC-keystone-roadmap-page-2026-07-26 R2 AC9): this
 * component receives all roadmap content as props (sourced from R1's
 * `roadmap-content.ts`) and contains NO auth, tenant, or data-fetch logic.
 * It manages only local UI state — the active filter chip and the
 * scroll-reveal-in state — so a future PUBLIC route can mount this exact
 * component with the exact same props and get an identical page. The authed
 * route wrapper (`../RoadmapPage.tsx`) is the only place that touches auth.
 *
 * Ported faithfully from the approved mockup:
 *   .metabus/specs/active/SPEC-keystone-roadmap-page-2026-07-26/R0-design-reference/roadmap-mockup.html
 *   .metabus/specs/active/SPEC-keystone-roadmap-page-2026-07-26/R0-design-reference/build_roadmap_generator.py
 *
 * Node semantics (no sequential numbers anywhere):
 *   - shipped  -> teal check mark
 *   - building -> amber pulsing dot
 *   - horizon  -> hollow dashed ring
 *
 * Visual grammar: `visual.kind === 'ui'` (a real product screenshot) renders
 * in a browser-chrome frame with a "Live in Keystone" badge, height-capped
 * with a soft bottom fade; `visual.kind === 'art'` (a generated illustration)
 * renders in a plain gradient card. Real screenshot == shipped, illustration
 * == coming — this pairing must be preserved when new items are added (R1).
 */
import { useEffect, useRef, useState } from 'react';
import type {
  RoadmapHero,
  RoadmapItem,
  RoadmapStatus,
  RoadmapTieback,
  RoadmapVisual,
} from './roadmap-content';
import './roadmap.css';

export type RoadmapFilter = 'all' | RoadmapStatus;

export interface RoadmapPageLayoutProps {
  hero: RoadmapHero;
  heroVisual: RoadmapVisual;
  shipped: RoadmapItem[];
  building: RoadmapItem[];
  horizon: RoadmapItem[];
  tieback: RoadmapTieback;
  /**
   * Optional handler for the tieback CTA click. When omitted, the CTA
   * renders as a plain anchor (`href="#"`, matching the mockup default) so
   * the component still works standalone with zero wiring — e.g. a future
   * public route, or this component's own isolated screenshot harness.
   */
  onTiebackCtaClick?: () => void;
}

// Chapter-plate copy is fixed structural labeling tied to the three status
// buckets themselves (not per-item curated content), so it lives here rather
// than in R1's content module. Verbatim from build_roadmap_generator.py.
const CHAPTER_COPY: Record<RoadmapStatus, { tag: string; name: string; sub: string }> = {
  shipped: { tag: 'Shipped', name: 'Recently shipped', sub: 'a few of the latest' },
  building: { tag: 'In progress', name: 'Building now', sub: 'actively in flight' },
  horizon: { tag: 'Planned', name: 'On the horizon', sub: 'further out' },
};

const FILTERS: { value: RoadmapFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'building', label: 'Building now' },
  { value: 'horizon', label: 'On the horizon' },
];

const CHECK_PATH = 'M5 13l4 4L19 7';

function Node({ status }: { status: RoadmapStatus }) {
  if (status === 'shipped') {
    return (
      <div className={`rm-node ${status}`}>
        <div className="rm-knob">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} aria-hidden="true">
            <path d={CHECK_PATH} />
          </svg>
        </div>
      </div>
    );
  }
  if (status === 'building') {
    return (
      <div className={`rm-node ${status}`}>
        <div className="rm-knob">
          <span className="rm-pulse-dot" aria-hidden="true" />
        </div>
      </div>
    );
  }
  return (
    <div className={`rm-node ${status}`}>
      <div className="rm-knob" aria-hidden="true" />
    </div>
  );
}

function StatPill({ status }: { status: RoadmapStatus }) {
  const label = { shipped: 'Shipped', building: 'Building now', horizon: 'Planned' }[status];
  return (
    <span className={`rm-stat ${status}`}>
      <span className="rm-d" aria-hidden="true" />
      {label}
    </span>
  );
}

function Visual({ item }: { item: RoadmapItem }) {
  const { visual } = item;
  if (visual.kind === 'ui') {
    return (
      <div className="rm-ui-frame">
        <div className="rm-ui-bar">
          <span className="rm-dot" aria-hidden="true" />
          <span className="rm-dot" aria-hidden="true" />
          <span className="rm-dot" aria-hidden="true" />
          <span className="rm-ui-url">keystone</span>
        </div>
        <div className="rm-ui-shot">
          <img src={visual.src} alt={visual.alt} loading="lazy" />
        </div>
        <span className="rm-live-badge">
          <span className="rm-dot" aria-hidden="true" />
          Live in Keystone
        </span>
      </div>
    );
  }
  return (
    <div className="rm-shot rm-art">
      <img src={visual.src} alt={visual.alt} loading="lazy" />
    </div>
  );
}

interface StopProps {
  item: RoadmapItem;
  side: 'r' | 'l';
  revealed: boolean;
  registerReveal: (id: string, el: HTMLElement | null) => void;
}

function Stop({ item, side, revealed, registerReveal }: StopProps) {
  const viz = <div className="rm-viz"><Visual item={item} /></div>;
  const body = (
    <div className="rm-mbody">
      <div className="rm-cat">{item.category}</div>
      <h3>{item.title}</h3>
      <p className="rm-hook">{item.hook}</p>
      <p className="rm-desc">{item.desc}</p>
      <StatPill status={item.status} />
    </div>
  );

  return (
    <div
      ref={(el) => registerReveal(`stop-${item.id}`, el)}
      className={`rm-stop rm-reveal ${item.status} ${side}${revealed ? ' rm-in' : ''}`}
    >
      <Node status={item.status} />
      <div className="rm-pair">
        {side === 'r' ? (
          <>
            {viz}
            {body}
          </>
        ) : (
          <>
            {body}
            {viz}
          </>
        )}
      </div>
    </div>
  );
}

function Chapter({
  status,
  revealed,
  registerReveal,
}: {
  status: RoadmapStatus;
  revealed: boolean;
  registerReveal: (id: string, el: HTMLElement | null) => void;
}) {
  const copy = CHAPTER_COPY[status];
  return (
    <div
      ref={(el) => registerReveal(`chapter-${status}`, el)}
      className={`rm-chapter rm-reveal${revealed ? ' rm-in' : ''}`}
      data-status={status}
    >
      <div className="rm-plate">
        <span className={`rm-ch-tag ${status}`}>{copy.tag}</span>
        <span className="rm-ch-name">{copy.name}</span>
        <span className="rm-ch-sub">{copy.sub}</span>
      </div>
    </div>
  );
}

export function RoadmapPageLayout({
  hero,
  heroVisual,
  shipped,
  building,
  horizon,
  tieback,
  onTiebackCtaClick,
}: RoadmapPageLayoutProps) {
  const [filter, setFilter] = useState<RoadmapFilter>('all');
  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());
  const trackRef = useRef<HTMLDivElement | null>(null);
  const revealElsRef = useRef<Map<string, HTMLElement>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const reducedMotionRef = useRef(false);

  const registerReveal = (id: string, el: HTMLElement | null) => {
    const map = revealElsRef.current;
    if (el) {
      el.dataset.revealId = id;
      map.set(id, el);
      observerRef.current?.observe(el);
    } else {
      const existing = map.get(id);
      if (existing) observerRef.current?.unobserve(existing);
      map.delete(id);
    }
  };

  const revealAll = () => {
    setRevealedIds(new Set(revealElsRef.current.keys()));
  };

  useEffect(() => {
    const prefersReduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    reducedMotionRef.current = prefersReduced;

    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      // Reduced motion (or no IO support): every tile is immediately visible,
      // no animated reveal.
      revealAll();
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        setRevealedIds((prev) => {
          let changed = false;
          const next = new Set(prev);
          for (const entry of entries) {
            if (entry.isIntersecting) {
              const id = (entry.target as HTMLElement).dataset.revealId;
              if (id && !next.has(id)) {
                next.add(id);
                changed = true;
              }
              observerRef.current?.unobserve(entry.target);
            }
          }
          return changed ? next : prev;
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -7% 0px' },
    );
    observerRef.current = io;
    revealElsRef.current.forEach((el) => io.observe(el));

    return () => {
      io.disconnect();
      observerRef.current = null;
    };
  }, []);

  const handleFilterClick = (value: RoadmapFilter) => {
    setFilter(value);
    // Matches the mockup: selecting a filter shows every remaining tile
    // immediately (no reveal-replay animation on the filtered set), so
    // nothing is left stranded invisible after filtering.
    revealAll();
    const smooth = !reducedMotionRef.current;
    trackRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
  };

  const isRevealed = (id: string) => revealedIds.has(id);

  return (
    <div className="roadmap-page rm-force-dark">
      <div className="rm-hero">
        <div className="rm-wrap rm-hero-in">
          <div>
            <span className="rm-eyebrow">Product roadmap</span>
            <h1 className="rm-h1">{hero.title}</h1>
            <p className="rm-lede">{hero.lede}</p>
            <div className="rm-meta">
              <span>{hero.chip}</span>
            </div>
          </div>
          <div className="rm-hero-art">
            <img src={heroVisual.src} alt={heroVisual.alt} />
          </div>
        </div>
      </div>

      <div className="rm-filters">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={filter === f.value ? 'rm-active' : ''}
            aria-pressed={filter === f.value}
            onClick={() => handleFilterClick(f.value)}
          >
            {f.value !== 'all' && <span className={`rm-fdot ${f.value}`} aria-hidden="true" />}
            {f.label}
          </button>
        ))}
      </div>

      <div className="rm-wrap">
        <div className="rm-track" data-filter={filter} ref={trackRef}>
          <div className="rm-spine" aria-hidden="true">
            <div className="rm-lit" />
            <div className="rm-ahead" />
          </div>

          <Chapter status="shipped" revealed={isRevealed('chapter-shipped')} registerReveal={registerReveal} />
          {shipped.map((item, i) => (
            <Stop
              key={item.id}
              item={item}
              side={i % 2 === 0 ? 'r' : 'l'}
              revealed={isRevealed(`stop-${item.id}`)}
              registerReveal={registerReveal}
            />
          ))}

          <div
            ref={(el) => registerReveal('here', el)}
            className={`rm-here rm-reveal${isRevealed('here') ? ' rm-in' : ''}`}
          >
            <span className="rm-badge">
              <span className="rm-live" aria-hidden="true" />
              You are here
            </span>
          </div>

          <Chapter status="building" revealed={isRevealed('chapter-building')} registerReveal={registerReveal} />
          {building.map((item, i) => (
            <Stop
              key={item.id}
              item={item}
              side={i % 2 === 0 ? 'r' : 'l'}
              revealed={isRevealed(`stop-${item.id}`)}
              registerReveal={registerReveal}
            />
          ))}

          <Chapter status="horizon" revealed={isRevealed('chapter-horizon')} registerReveal={registerReveal} />
          {horizon.map((item, i) => (
            <Stop
              key={item.id}
              item={item}
              side={i % 2 === 0 ? 'r' : 'l'}
              revealed={isRevealed(`stop-${item.id}`)}
              registerReveal={registerReveal}
            />
          ))}

        </div>
      </div>

      <div className="rm-wrap">
        <div
          ref={(el) => registerReveal('tieback', el)}
          className={`rm-tieback rm-reveal${isRevealed('tieback') ? ' rm-in' : ''}`}
        >
          <div>
            <h4>{tieback.heading}</h4>
            <p>{tieback.body}</p>
          </div>
          {onTiebackCtaClick ? (
            <button type="button" className="rm-btn" onClick={onTiebackCtaClick}>
              {tieback.ctaLabel}
            </button>
          ) : (
            <a className="rm-btn" href="#">
              {tieback.ctaLabel}
            </a>
          )}
        </div>
      </div>

      <div className="rm-wrap">
        <footer className="rm-footer">
          <span>KEYSTONE / ROADMAP</span>
        </footer>
      </div>
    </div>
  );
}
